// A small force-directed layout, written out rather than installed.
//
// The graph is forty-odd nodes and will not soon be thousands; a physics
// library would be the largest thing in this bundle by an order of magnitude,
// for a picture that settles in half a second. This is Fruchterman–Reingold:
// every node pushes every other away, every edge pulls its two ends together,
// and the whole thing cools.
//
// It is SEEDED deterministically, so the same web lays out the same way every
// time. A map whose shape changes on reload is not a map.

const AREA = 900;

// A stable pseudo-random from a string, so a node's starting place depends on
// what it IS rather than on when it happened to be loaded.
function hash(key) {
  let value = 2166136261;
  for (let index = 0; index < key.length; index += 1) {
    value ^= key.charCodeAt(index);
    value = Math.imul(value, 16777619);
  }
  return (value >>> 0) / 4294967295;
}

export function createLayout(focusKey) {
  return { positions: new Map(), alpha: 1, focus: focusKey };
}

// New nodes appear NEXT TO the node they were expanded from and are pushed out
// by the simulation, which is what makes an expansion read as growth rather
// than as a redraw.
export function seed(layout, key, origin) {
  if (layout.positions.has(key)) return;
  const angle = hash(key) * Math.PI * 2;
  const distance = origin ? 40 : 120 + (hash(`${key}r`) * 120);
  layout.positions.set(key, {
    x: (origin?.x || 0) + (Math.cos(angle) * distance),
    y: (origin?.y || 0) + (Math.sin(angle) * distance),
    vx: 0,
    vy: 0
  });
}

export function reheat(layout, alpha = 0.9) {
  layout.alpha = Math.max(layout.alpha, alpha);
}

export function tick(layout, nodeKeys, edges) {
  if (layout.alpha < 0.005) return false;

  const k = Math.sqrt(AREA / Math.max(nodeKeys.length, 1)) * 6;
  const points = nodeKeys.map((key) => layout.positions.get(key)).filter(Boolean);

  // Everything pushes everything away.
  for (let i = 0; i < points.length; i += 1) {
    for (let j = i + 1; j < points.length; j += 1) {
      const a = points[i];
      const b = points[j];
      let dx = a.x - b.x;
      let dy = a.y - b.y;
      let distance = Math.hypot(dx, dy);
      if (distance < 0.01) {
        // Two nodes exactly on top of each other have no direction to separate
        // in; nudge them deterministically rather than leaving them fused.
        dx = (i % 2 ? 1 : -1) * 0.5;
        dy = 0.5;
        distance = 0.7;
      }
      const force = (k * k) / distance;
      const fx = (dx / distance) * force;
      const fy = (dy / distance) * force;
      a.vx += fx; a.vy += fy;
      b.vx -= fx; b.vy -= fy;
    }
  }

  // Edges pull their ends together.
  edges.forEach((edge) => {
    const a = layout.positions.get(edge.from);
    const b = layout.positions.get(edge.to);
    if (!a || !b) return;
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    const distance = Math.max(Math.hypot(dx, dy), 0.01);
    const force = (distance * distance) / k;
    const fx = (dx / distance) * force;
    const fy = (dy / distance) * force;
    a.vx -= fx; a.vy -= fy;
    b.vx += fx; b.vy += fy;
  });

  // A weak pull to the middle, so a component that has drifted comes back.
  points.forEach((point) => {
    point.vx -= point.x * 0.012;
    point.vy -= point.y * 0.012;
  });

  const step = layout.alpha * 0.28;
  points.forEach((point) => {
    const speed = Math.hypot(point.vx, point.vy);
    const limit = 90;
    const scale = speed > limit ? limit / speed : 1;
    point.x += point.vx * scale * step;
    point.y += point.vy * scale * step;
    point.vx = 0;
    point.vy = 0;
  });

  // The focused node holds the centre so the map does not wander out from
  // under the thing you are looking at.
  if (layout.focus && layout.positions.has(layout.focus)) {
    const centre = layout.positions.get(layout.focus);
    const dx = centre.x;
    const dy = centre.y;
    points.forEach((point) => { point.x -= dx * 0.5; point.y -= dy * 0.5; });
  }

  layout.alpha *= 0.94;
  return true;
}

// Everything settled at once, for a reader who has asked not to be animated.
export function settle(layout, nodeKeys, edges, ticks = 300) {
  layout.alpha = 1;
  for (let index = 0; index < ticks; index += 1) tick(layout, nodeKeys, edges);
  layout.alpha = 0;
}

export function bounds(layout, nodeKeys) {
  const points = nodeKeys.map((key) => layout.positions.get(key)).filter(Boolean);
  if (points.length === 0) return { x: -100, y: -100, width: 200, height: 200 };
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const pad = 90;
  const minX = Math.min(...xs) - pad;
  const minY = Math.min(...ys) - pad;
  return {
    x: minX,
    y: minY,
    width: Math.max(...xs) - minX + pad,
    height: Math.max(...ys) - minY + pad
  };
}
